import React from 'react';
import { Tooltip } from '@material-ui/core';
import withShipment from '../../../withShipment';
import { viewOnlySelector } from '../../../redux/selectors/auth';
import { VIEW_ONLY_MESSAGE } from '../../../constants/readOnlyAccess';
import './ReadOnly.css';

/**
 * Removes the write controls inside it when the user is operating a
 * VIEW-ONLY location.
 *
 * DEFAULT IS TO HIDE, not to grey out. A disabled Add button still
 * invites the user to look for a way in; worse, a greyed Save at the end
 * of a form means they fill the whole thing in before anything tells them
 * it won't stick. Taking the affordance away at the point where the
 * journey starts is the honest version.
 *
 *   <WriteGuard>
 *     <Button onClick={this.save}>Save</Button>
 *   </WriteGuard>
 *
 * Use `mode="disable"` only where removing the control would break the
 * surrounding layout or leave the screen unreadable — a toolbar that
 * would otherwise collapse, or a control whose absence would be more
 * confusing than its presence. In that mode the subtree is greyed and
 * made inert via a native `<fieldset disabled>` (the browser disables
 * every descendant form control for free) plus `pointer-events: none`
 * for the things that aren't form elements — MUI IconButtons rendered as
 * divs, clickable rows, drop zones.
 *
 * Inside a table row, wrap the CONTROL and leave the surrounding
 * <TableCell> in place, so hiding it doesn't shift the columns out of
 * line with their headers.
 *
 * The server refuses the write regardless (ReadOnlyGateInterceptor), and
 * AuthUtil refuses it before it leaves the browser. This is about not
 * offering the action in the first place.
 */
class WriteGuard extends React.Component {
  render() {
    const {
      viewOnly,
      children,
      // 'hide' (default) removes the subtree; 'disable' greys it out and
      // makes it inert.
      mode = 'hide',
      // Suppress the explanatory tooltip in 'disable' mode (e.g. inside a
      // table cell, where a tooltip on every row would be noise).
      noTooltip = false,
      className = '',
      // Inline style on the wrapper — handy when the guard sits in a flex
      // row and needs to keep its parent's layout.
      style,
      tooltip = VIEW_ONLY_MESSAGE,
    } = this.props;

    if (!viewOnly) return children || null;
    if (mode !== 'disable') return null;

    const guarded = (
      <fieldset
        className={`nuve-write-guard ${className}`.trim()}
        style={style}
        disabled
        aria-disabled="true"
      >
        {children}
      </fieldset>
    );

    if (noTooltip) return guarded;

    // The tooltip needs an element that accepts a ref and still receives
    // mouse events — everything inside the fieldset is pointer-events:none,
    // so the span wrapper is what the pointer actually hits.
    return (
      <Tooltip title={tooltip} placement="top">
        <span className="nuve-write-guard__anchor">{guarded}</span>
      </Tooltip>
    );
  }
}

const mapStateToProps = (state) => ({
  viewOnly: viewOnlySelector(state),
});

export default withShipment({ mapStateToProps, actionCreators: {} }, WriteGuard);
